<template>
  <!-- Proposal wrapper -->
  <div
    :key="reloadKey"
    class="is-public w-full overflow-x-hidden">
    <LoadingModal
      heading="Loading Proposal"
      :loading="loading" />
    <ProposalStatusInfo
      v-show="showFloatingControls"
      :accepted="store.proposal.accepted || !!store.proposal.accepted_sibling_id"
      :is-sibling="!!store.proposal.accepted_sibling_id"
      :version="proposalVersions?.find(v => v.proposalVersionId === store.proposal.proposalVersionId)?.proposalVersionNumber ?? 0"
      :version-id="proposalVersions?.find(v => v.proposalVersionId === store.proposal.proposalVersionId)?.proposalVersionId ?? 0"
      :sibling-version="proposalVersions?.find(v => v.proposalVersionId === store.proposal.accepted_sibling_id)?.proposalVersionNumber ?? 0"
      :sibling-version-id="proposalVersions?.find(v => v.proposalVersionId === store.proposal.accepted_sibling_id)?.proposalVersionId ?? 0"
      :order="1" />
    <ProposalLanguageSelector
      v-show="showFloatingControls"
      v-bind="$attrs"
      :order="2" />
    <!-- Content sections -->
    <ProposalHero @scroll-into-view="(v) => $refs[v.component].$el.scrollIntoView({ behavior: 'smooth' })" />
    <div
      ref="afterHeroSentinel"
      aria-hidden="true"
      class="size-0" />
    <ProposalIntro ref="ProposalIntroComponent" />
    <ProposalTransport ref="ProposalTransportComponent" />
    <ProposalLodgings ref="ProposalLodgingsComponent" />
    <ProposalProgram ref="ProposalProgramComponent" />
    <ProposalActivities ref="ProposalActivitiesComponent" />
    <ProposalEateries ref="ProposalEateriesComponent" />
    <ProposalMediaGallery ref="ProposalMediaGalleryComponent" />
    <!-- // TODO: Create ProposalInteractiveMap when dynamic data is available -->
    <!-- <ProposalInteractiveMap ref="ProposalInteractiveMapComponent" /> -->
    <ProposalPrices
      ref="ProposalPricesComponent"
      :proposal-versions="proposalVersions" />
  </div>
  <!-- /Proposal wrapper -->
</template>

<script setup>
/* external */
import {
  onBeforeMount, onBeforeUnmount,
  onMounted, ref, watch,
} from 'vue';

/* local */
import { useProposalStore } from '@/Stores/Proposal';
import { currentLanguage } from '@/utils';
import ProposalHero from '@/Pages/Proposal/Show/ProposalHero.vue';
import ProposalIntro from '@/Pages/Proposal/Show/ProposalIntro.vue';
import ProposalTransport from '@/Pages/Proposal/Show/ProposalTransport.vue';
import ProposalLodgings from '@/Pages/Proposal/Show/ProposalLodgings.vue';
import ProposalProgram from '@/Pages/Proposal/Show/ProposalProgram.vue';
import ProposalActivities from '@/Pages/Proposal/Show/ProposalActivities.vue';
import ProposalEateries from '@/Pages/Proposal/Show/ProposalEateries.vue';
import ProposalMediaGallery from '@/Pages/Proposal/Show/ProposalMediaGallery.vue';
import ProposalPrices from '@/Pages/Proposal/Show/ProposalPrices.vue';
import LoadingModal from '@/Components/Modals/LoadingModal.vue';
import ProposalLanguageSelector from '@/Pages/Proposal/Components/ProposalLanguageSelector.vue';
import ProposalStatusInfo from '@/Pages/Proposal/Components/ProposalStatusInfo.vue';

/* *********************************************************************
 * Page declarations
 * ****************************************************************** */

const props = defineProps({
  databaseProposal: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  databaseProposalVersions: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const reloadKey = ref(0);

const store = useProposalStore();

const ProposalIntroComponent = ref(null);
const ProposalTransportComponent = ref(null);
const ProposalLodgingsComponent = ref(null);
const ProposalProgramComponent = ref(null);
const ProposalPricesComponent = ref(null);

// Minimal visibility control using IntersectionObserver
const showFloatingControls = ref(false);
const afterHeroSentinel = ref(null);
let io = null;

onMounted(() => {
  const root = document.querySelector('main[tabindex="0"]');
  io = new IntersectionObserver(([entry]) => {
    showFloatingControls.value = entry ? !entry.isIntersecting : false;
  }, {
    root,
    threshold: 0,
  });
  if (afterHeroSentinel.value) {
    io.observe(afterHeroSentinel.value);
  }
});

onBeforeUnmount(() => {
  if (io) {
    io.disconnect();
  }
  io = null;
});

const proposalVersions = ref(props.databaseProposalVersions);

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

// loading should only be active on first load
const loading = ref(document.readyState === 'interactive' ?? false);

onBeforeMount(() => {
  // Reset store
  store.$reset();
  // Set store with data from database
  store.setProposal({
    ...props.databaseProposal.data,
    accepted: props.databaseProposal.accepted,
    accepted_at: props.databaseProposal.accepted_at,
    verification_email_sent: props.databaseProposal.verification_email_sent,
    accepted_data: props.databaseProposal.accepted_data,
    terms: props.databaseProposal.terms,
    hash: props.databaseProposal.hash,
    proposalVersionId: props.databaseProposal.id,
    accepted_sibling_id: props.databaseProposal.accepted_sibling_id,
  }, true);
});

onMounted(() => {
  // LoadingModal - if first load, we need to stop loading when completed
  document.onreadystatechange = () => {
    if (document.readyState === 'complete') {
      setTimeout(() => {
        loading.value = false;
      }, 500);
    }
  };
});

// When language changes, we refresh the DOM tree to update all components
watch(currentLanguage, () => {
  reloadKey.value += 1;
});

onBeforeUnmount(() => {
  // Reset store
  store.$reset();
});
</script>

<script>
/* eslint-disable */
  import EmptyLayout from '@/Layouts/EmptyLayout';
  export default { layout: EmptyLayout };
/* eslint-enable */
</script>
